import type { FC, PropsWithChildren } from 'react'
import React, { useState } from 'react'
import styled from '@emotion/styled'

import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import { AppearanceProvider } from '@segment/matcha/foundations/AppearanceContext'

import Button from '@segment/matcha/components/Button'

import TextArea from '@segment/matcha/components/TextArea'

import Input from '@segment/matcha/components/Input'

import Loader from '@segment/matcha/components/Loader'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import Footer from '@segment/templates/Footer'
import Navigation from '@segment/templates/Navigation'
import { CompanyEmailField, companyEmailMessages, FullNameField, fullNameMessages } from '@segment/templates/FormFields'
import FormSubmitAction from '@segment/templates/FormSubmitAction'
import TrackableForm, { errorMessage } from '@segment/templates/TrackableForm'

import useScrollToAnchor from '@segment/utils/useScrollToAnchor'

import emailRegex from '@segment/utils/regexes/email'
import formRegex from '@segment/utils/regexes/form'

import nameRegex from '@segment/utils/regexes/name'

import Layout from '@segment/next-shared/layout'

import type { FormStatus } from './helper'
import submitContactBillingForm from './helper'

import ogImageJpeg from './assets/OgBilling.jpeg'

const slugMessages = {
  required: `Please enter your workspace slug.`,
  invalid: `Please enter a valid workspace slug.`,
}

const helpMessages = {
  required: `Please describe your issue.`,
  invalid: `Please provide a valid issue: no special characters.`,
}

const schema = z.object({
  email: z
    .string()
    .min(1, { message: companyEmailMessages.required })
    .regex(formRegex, { message: companyEmailMessages.invalid })
    .regex(emailRegex, {
      message: companyEmailMessages.invalid,
    }) /* This is equal to .email( { message: companyEmailMessages.invalid }) but with a custom regex */,
  slug: z.string().min(1, { message: slugMessages.required }).regex(formRegex, { message: slugMessages.invalid }),
  name: z
    .string()
    .min(1, { message: fullNameMessages.required })
    .regex(formRegex, { message: fullNameMessages.invalid }),
  help: z.string().min(1, { message: helpMessages.required }).regex(formRegex, { message: helpMessages.invalid }),
})

const copy = {
  formHeading: `Contact Support`,
  formDescription: `How can we help you?`,
  formButton: `Contact Support`,
  successResponseHeading: `Thank you`,
  successResponseDescription: `We'll get back to you shortly.`,
}

const Billing: FC<PropsWithChildren<unknown>> = () => {
  const methods = useForm({
    mode: `onBlur`,
    reValidateMode: `onBlur`,
    shouldUnregister: true,
    resolver: zodResolver(schema),
  })
  const scroll = useScrollToAnchor(`billing-form`)
  const [formStatus, setFormStatus] = useState<FormStatus>(`idle`)
  const [valid, setValid] = useState(false)
  const {
    register,
    trigger,
    formState: { errors },
  } = methods

  return (
    <AppearanceProvider appearance="twilioLight">
      <Layout
        pageTitle="Contact | Twilio Segment"
        pageDescription="Twilio Segment is a customer data platform (CDP) that helps you collect, clean, and activate your customer data."
        pageOG={ogImageJpeg}
      >
        <Navigation />
        <Main id="billing-form">
          <StyledSectionBase variant="centered">
            <Section.Copy>
              <CopyTextBase centered={true}>
                <CopyText.Heading tag="h1" variant="h1">
                  {copy.formHeading}
                </CopyText.Heading>
                <CopyText.Description tag="h2" variant="pLarge">
                  {copy.formDescription}
                </CopyText.Description>
              </CopyTextBase>
            </Section.Copy>
            <Section.Contents>
              <StyledForm
                methods={methods}
                gap="small"
                onSubmit={(formData) => {
                  scroll()

                  submitContactBillingForm(formData).then(setFormStatus)
                }}
                success={
                  formStatus === `idle` ? (
                    <LoaderWrapper>
                      <Loader />
                    </LoaderWrapper>
                  ) : (
                    <FormMessageWrapper>
                      <FormSubmitAction
                        data-hook="billing-form-response-message"
                        heading={copy.successResponseHeading}
                        description={copy.successResponseDescription}
                        status="success"
                        appearance="twilioLight"
                      />
                    </FormMessageWrapper>
                  )
                }
                error={<FormSubmitAction status="error" {...errorMessage} />}
              >
                <CompanyEmailField label="Your billing email" placeholder="you@company.com" />
                <Input
                  id="slug"
                  placeholder="hitchhikers-gtg"
                  label="Your workspace slug"
                  type="text"
                  error={Boolean(errors.slug?.message)}
                  errorText={errors.slug?.message}
                  expanded={true}
                  showValidState={valid && Boolean(!errors.slug?.message)}
                  onAutoFill={(value) => {
                    setValid(nameRegex.test(value))
                    trigger(`slug`)
                  }}
                  {...register(`slug`, {
                    required: slugMessages.required,
                    onBlur(e) {
                      setValid(nameRegex.test(e.target.value))
                    },
                  })}
                />
                <FullNameField label="Your name" />
                <TextArea
                  id="help"
                  expanded={true}
                  label="How can we help?"
                  placeholder="I need help with..."
                  error={Boolean(errors.help?.message)}
                  errorText={errors.help?.message}
                  maxWidth="855px"
                  rows={4}
                  {...register(`help`, {
                    required: helpMessages.required,
                  })}
                />
                <SubmitButton color="primary" variant="filled" size="large">
                  {copy.formButton}
                </SubmitButton>
              </StyledForm>
            </Section.Contents>
          </StyledSectionBase>
        </Main>
      </Layout>
      <Footer />
    </AppearanceProvider>
  )
}

const Main = styled.main`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledSectionBase = styled(SectionBase)`
  ${Section.Wrapper} {
    margin: 0 auto;
    max-width: 940px;
    grid-template-columns: 1fr;
    row-gap: 40px;

    ${({ theme }) => theme.breakpoints.up(`md`)} {
      row-gap: 64px;
    }
  }
`

const SubmitButton = styled(Button)`
  margin-top: 20px;
`

const FormMessageWrapper = styled.div`
  max-width: 410px;
  margin: 0 auto;
`

const StyledForm = styled(TrackableForm)`
  min-height: 200px;
`

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  min-height: 180px;
  align-items: center;
`

export default Billing
